import type { ReactNode } from "react";

import { Badge2 } from "@/ui2/atoms/Badge";
import { Button2 } from "@/ui2/atoms/Button";
import {
  Timeline,
  TimelineDetail,
  TimelineDetails,
  TimelineGroup,
  TimelineItem,
  TimelineSkeleton,
  TimelineTimestamp,
} from "@/ui2/molecules/Timeline";
import type { UUID } from "@/core/types";

import { COSIGNATURE_ACKNOWLEDGEMENT } from "../constants";
import { formatHistoryAuthor, formatHistorySupervisor } from "../helpers/history-person";
import type { NoteHistoryGroup, NoteHistoryGroupEvent } from "../helpers/adapters";
import type { NoteHistoryViewAs } from "../types";
import { useNoteHistory } from "../api/queries/useNoteHistory";

import { NoteHistoryStatusBadge } from "./NoteHistoryStatusBadge";

type NoteHistoryTimelineProps = {
  noteId: UUID;
  viewAs: NoteHistoryViewAs;
};

export function NoteHistoryTimeline({ noteId, viewAs }: NoteHistoryTimelineProps) {
  const {
    data: groups,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useNoteHistory({ noteId, viewAs });

  if (isLoading) return <TimelineSkeleton />;

  if (isError) {
    return (
      <div
        className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:px-5 tw:py-10 tw:text-center"
        role="alert"
      >
        <p>Unable to load co-signature history. Please try again.</p>
        <Button2 isLoading={isFetching} onPress={() => void refetch()} variant="secondary">
          Try again
        </Button2>
      </div>
    );
  }

  if (!groups?.length) {
    return (
      <output className="body-lg tw:flex tw:w-full tw:px-5 tw:py-10 tw:text-center">
        No co-signature history available.
      </output>
    );
  }

  return (
    <Timeline>
      {groups.map((group) => (
        <DocumentGroup {...group} key={group.documentId} />
      ))}
    </Timeline>
  );
}

function DocumentGroup({ documentId, events, groupLabel }: NoteHistoryGroup) {
  return (
    <>
      <TimelineGroup>{groupLabel}</TimelineGroup>

      {events.map((event, index) => (
        <EventRecord
          event={event}
          isLatest={index === events.length - 1}
          key={`${documentId}-${event.cosignature_status}-${event.timestamp}-${event.document_version}`}
        />
      ))}
    </>
  );
}

function EventRecord({ event, isLatest }: { event: NoteHistoryGroupEvent; isLatest: boolean }) {
  return (
    <TimelineItem isLatest={isLatest}>
      <div className="tw:flex tw:min-w-40 tw:flex-col tw:gap-y-1 tw:border-r tw:border-fill-quaternary tw:pr-4">
        <TimelineTimestamp dateTime={event.timestamp} />
        <Badge2
          appearance={isLatest ? "solid" : "outline"}
          className="tw:mt-0 tw:self-baseline"
          size="sm"
          variant="neutral"
        >
          version {event.document_version}
        </Badge2>
      </div>

      <TimelineDetails>
        <TimelineDetail
          label="Status"
          value={
            <NoteHistoryStatusBadge
              cosignature_status={event.cosignature_status}
              label={event.statusLabel}
            />
          }
        />

        {getEventDetailRows(event).map((row) => (
          <TimelineDetail key={row.label} label={row.label} value={row.value} />
        ))}
      </TimelineDetails>
    </TimelineItem>
  );
}

function getEventDetailRows(event: NoteHistoryGroupEvent): { label: string; value: ReactNode }[] {
  let rows: { label: string; value: ReactNode }[];

  switch (event.cosignature_status) {
    case "pending":

    case "resubmitted":
      rows = [
        {
          label: "Signed by",
          value: formatHistoryAuthor({
            credentials: event.provider_credentials,
            name: event.provider_name,
            signature: event.signature,
          }),
        },
        {
          label: "Assigned to",
          value: formatHistorySupervisor({
            credentials: event.assigned_supervisor_credentials,
            name: event.assigned_supervisor_name,
          }),
        },
      ];
      break;

    case "rejected":
      rows = [
        {
          label: "Rejected by",
          value: formatHistorySupervisor({
            credentials: event.reviewing_supervisor_credentials,
            name: event.reviewing_supervisor_name,
          }),
        },
        { label: "Rejection reason", value: event.rejection_reason },
      ];
      break;

    case "signed":
      rows = [
        {
          label: "Co-signed by",
          value: formatHistorySupervisor({
            credentials: event.reviewing_supervisor_credentials,
            name: event.reviewing_supervisor_name,
          }),
        },
        {
          label: "Acknowledgment",
          value: <em>{COSIGNATURE_ACKNOWLEDGEMENT}</em>,
        },
        { label: "Supervisor comment", value: event.acknowledgment_details },
      ];
      break;
  }

  return rows.filter(
    (row) =>
      row.value !== undefined &&
      row.value !== null &&
      (typeof row.value !== "string" || row.value.trim() !== ""),
  );
}
